/**
 * LERA Compatibility Finder — UI adapter (first Engine consumer).
 * Contains no business logic. Delegates everything to window.LERA.compatibility.
 *
 * DCF-001: the model field is a dynamic <select>. Choosing a brand loads that
 * brand's index and fills the second selector with only its models.
 */
(function (global) {
  'use strict';

  var Engine = global.LERA && global.LERA.compatibility;

  function parseConfig(root) {
    var configElement = root.querySelector('[data-compatibility-config]');
    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('LERA Compatibility Finder UI: invalid configuration JSON.', error);
      return null;
    }
  }

  function CompatibilityFinderUI(root) {
    if (!root || !Engine) return;

    this.root = root;
    this.config = parseConfig(root);
    if (!this.config) return;

    Engine.init(this.config);

    this.brandSelect = root.querySelector('[data-compatibility-brand]');
    this.modelSelect = root.querySelector('[data-compatibility-model]');
    this.submitButton = root.querySelector('[data-compatibility-submit]');
    this.loadingMessage = root.querySelector('[data-compatibility-loading]');
    this.emptyMessage = root.querySelector('[data-compatibility-empty]');
    this.errorMessage = root.querySelector('[data-compatibility-error]');
    this.statusRegion = root.querySelector('[data-compatibility-status]');
    this.diagnosticsPanel = root.querySelector('[data-compatibility-diagnostics]');

    if (!this.brandSelect || !this.modelSelect || !this.submitButton) return;

    this.selectedBrandId = '';
    this.selectedModel = '';
    this.loadRequestId = 0;

    this.bindEvents();
    this.resetModelField();
    this.populateBrands();
    this.restoreDeviceContext();
    this.renderDiagnostics();
  }

  CompatibilityFinderUI.prototype.announce = function (message) {
    if (!this.statusRegion || !message) return;
    this.statusRegion.textContent = message;
  };

  CompatibilityFinderUI.prototype.populateBrands = function () {
    var brands = Engine.getBrands();
    var placeholder = this.brandSelect.getAttribute('data-placeholder') || '';

    this.brandSelect.innerHTML = '';
    var placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    this.brandSelect.appendChild(placeholderOption);

    brands.forEach(
      function (brand) {
        var option = document.createElement('option');
        option.value = brand.id;
        option.textContent = brand.name;
        this.brandSelect.appendChild(option);
      }.bind(this)
    );
  };

  /** Fills the model <select> with the given display names (placeholder first). */
  CompatibilityFinderUI.prototype.populateModels = function (models) {
    var placeholder = this.modelSelect.getAttribute('data-placeholder') || '';

    this.modelSelect.innerHTML = '';
    var placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.textContent = placeholder;
    this.modelSelect.appendChild(placeholderOption);

    (models || []).forEach(
      function (model) {
        var option = document.createElement('option');
        option.value = model;
        option.textContent = model;
        this.modelSelect.appendChild(option);
      }.bind(this)
    );
  };

  CompatibilityFinderUI.prototype.setSubmitEnabled = function (enabled) {
    this.submitButton.disabled = !enabled;
    this.submitButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  CompatibilityFinderUI.prototype.setModelEnabled = function (enabled) {
    this.modelSelect.disabled = !enabled;
    this.modelSelect.setAttribute('aria-disabled', enabled ? 'false' : 'true');
  };

  CompatibilityFinderUI.prototype.showMessage = function (type, visible) {
    var map = {
      loading: this.loadingMessage,
      empty: this.emptyMessage,
      error: this.errorMessage,
    };

    Object.keys(map).forEach(function (key) {
      if (!map[key]) return;
      map[key].hidden = key === type ? !visible : true;
    });
  };

  CompatibilityFinderUI.prototype.resetModelField = function () {
    this.selectedModel = '';
    this.populateModels([]);
    this.setModelEnabled(false);
    this.setSubmitEnabled(false);
    this.showMessage('loading', false);
    this.showMessage('empty', false);
    this.showMessage('error', false);
    this.modelSelect.setAttribute('aria-busy', 'false');
  };

  CompatibilityFinderUI.prototype.clearResults = function () {
    var panel = this.root.querySelector('[data-compatibility-results]');
    var grid = this.root.querySelector('[data-compatibility-results-grid]');
    var status = this.root.querySelector('[data-compatibility-results-status]');
    if (panel) panel.hidden = true;
    if (grid) grid.innerHTML = '';
    if (status) {
      status.hidden = true;
      status.textContent = '';
    }
  };

  CompatibilityFinderUI.prototype.onBrandChange = function () {
    this.selectedBrandId = this.brandSelect.value;
    this.resetModelField();
    this.clearResults();

    if (!this.selectedBrandId) return;

    this.showMessage('loading', true);
    this.modelSelect.setAttribute('aria-busy', 'true');
    var requestId = ++this.loadRequestId;

    Engine.loadBrandIndex(this.selectedBrandId).then(
      function (result) {
        if (requestId !== this.loadRequestId) return;

        this.modelSelect.setAttribute('aria-busy', 'false');
        this.showMessage('loading', false);

        if (!result.success) {
          this.showMessage('empty', true);
          if (this.emptyMessage && result.userMessage) {
            this.emptyMessage.textContent = result.userMessage;
          }
          this.announce(result.userMessage || '');
          return;
        }

        var models = Engine.getModels(this.selectedBrandId);

        if (!models.length) {
          this.showMessage('empty', true);
          this.announce('0 modelos disponibles.');
          return;
        }

        this.populateModels(models);
        this.setModelEnabled(true);
        this.announce(models.length + ' modelos disponibles.');
      }.bind(this)
    );
  };

  CompatibilityFinderUI.prototype.onModelChange = function () {
    this.selectedModel = this.modelSelect.value;
    this.setSubmitEnabled(Boolean(this.selectedBrandId && this.selectedModel));

    if (this.selectedModel) {
      this.showMessage('empty', false);
      this.showMessage('error', false);
      this.announce(this.selectedModel + ' seleccionado.');
    }
  };

  CompatibilityFinderUI.prototype.onSubmit = function () {
    if (this.submitButton.disabled || !this.selectedBrandId || !this.selectedModel) return;

    var brands = Engine.getBrands();
    var brand = null;
    for (var brandIndex = 0; brandIndex < brands.length; brandIndex += 1) {
      if (brands[brandIndex].id === this.selectedBrandId) {
        brand = brands[brandIndex];
        break;
      }
    }

    Engine.setCurrentDevice({
      brandId: this.selectedBrandId,
      brand: brand ? brand.name : '',
      model: this.selectedModel,
      source: 'finder',
    });

    // LCE-003: render compatible products in-place (no S&D / collection filter).
    var resultsApi = global.LERA && global.LERA.compatibilityResults;
    if (resultsApi && typeof resultsApi.render === 'function' && this.root.querySelector('[data-compatibility-results]')) {
      resultsApi.render({
        root: this.root,
        model: this.selectedModel,
      });
      return;
    }

    var redirect = Engine.buildRedirect(this.selectedBrandId, this.selectedModel);
    if (redirect.success && redirect.url) {
      window.location.href = redirect.url;
    }
  };

  CompatibilityFinderUI.prototype.restoreDeviceContext = function () {
    var currentDevice = Engine.getCurrentDevice();
    if (!currentDevice || !currentDevice.brandId) return;

    this.brandSelect.value = currentDevice.brandId;
    this.selectedBrandId = currentDevice.brandId;

    Engine.loadBrandIndex(currentDevice.brandId).then(
      function (result) {
        if (!result.success) return;

        this.populateModels(Engine.getModels(currentDevice.brandId));
        this.setModelEnabled(true);
        this.modelSelect.value = currentDevice.model || '';
        this.onModelChange();
      }.bind(this)
    );
  };

  CompatibilityFinderUI.prototype.renderDiagnostics = function () {
    if (!this.diagnosticsPanel || !this.config.enableDiagnostics) return;

    var diagnostics = Engine.getDiagnostics();
    var globalStats = Engine.getGlobalStats ? Engine.getGlobalStats() : null;
    var lines = [
      'Engine initialized: ' + diagnostics.initialized,
      'Catalog source: ' + diagnostics.catalogSource,
      'Global index built: ' + diagnostics.globalIndexBuilt,
      'Provider status: ' + diagnostics.providerStatus,
      'Products read: ' + diagnostics.productsRead,
      'Variants read: ' + diagnostics.variantsRead,
      'Global models indexed: ' + (globalStats ? globalStats.modelCount : diagnostics.modelsIndexed),
      'Brands found: ' + diagnostics.brandsFound,
      'Aliases loaded: ' + diagnostics.aliasesLoaded,
      'Brand views indexed: ' + diagnostics.modelsIndexed,
      'Cache status: ' + diagnostics.cacheStatus,
      'Cache age (min): ' + (diagnostics.cacheAgeMinutes == null ? 'n/a' : diagnostics.cacheAgeMinutes),
      'Redirect mode: ' + diagnostics.redirectMode,
      'Current device: ' + (diagnostics.currentDevice ? diagnostics.currentDevice.brand + ' / ' + diagnostics.currentDevice.model : 'none'),
      'Warnings: ' + diagnostics.warnings.join(' | '),
      'Success: ' + diagnostics.success.join(' | '),
    ];

    this.diagnosticsPanel.textContent = lines.join('\n');
  };

  CompatibilityFinderUI.prototype.bindEvents = function () {
    this.brandSelect.addEventListener('change', this.onBrandChange.bind(this));
    this.modelSelect.addEventListener('change', this.onModelChange.bind(this));
    this.submitButton.addEventListener('click', this.onSubmit.bind(this));
  };

  function initRoot(root) {
    if (!root || root.getAttribute('data-compatibility-initialized') === 'true') return;

    if (!root.id) {
      root.id = 'lera-compatibility-finder-' + Math.random().toString(36).slice(2, 9);
    }

    root.setAttribute('data-compatibility-initialized', 'true');
    new CompatibilityFinderUI(root);
  }

  function boot() {
    document.querySelectorAll('[data-compatibility-finder]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;

    section.querySelectorAll('[data-compatibility-finder]').forEach(function (root) {
      root.removeAttribute('data-compatibility-initialized');
      initRoot(root);
    });
  });
})(typeof window !== 'undefined' ? window : this);
